import * as React from 'react';

/**
 * DomainAnnouncer — a visually-hidden ARIA live region that announces a
 * meaningful *domain* transition to assistive technology, mirroring the proven
 * `role="status"` pattern in alarms' `AlarmCountBadge` but generalized so any
 * regulated / real-time surface can voice its key state change without painting
 * a visible widget.
 *
 * Usage: pass the current domain text (e.g. "Interlock SI-12 engaged",
 * "NCR-4471 moved to Disposition"). The region re-announces only when the text
 * actually changes — a steady value is not re-read on every render — so the
 * operator hears the transition, not a chattering control.
 *
 * - `assertive` (default false) maps to `role="alert"` / `aria-live="assertive"`
 *   for safety- or compliance-critical transitions that must interrupt
 *   (interlock engaged, sanctions hit, site failover). Otherwise `role="status"`
 *   / `aria-live="polite"` for routine domain transitions (equipment state, NCR
 *   phase, lot hold/release).
 * - `aria-atomic` is set so the full message is read as one unit.
 *
 * Render it once near the top of a view; it never affects layout.
 */
export interface DomainAnnouncerProps {
  /**
   * The current domain transition text. Pass an empty string / null to stay
   * silent (e.g. before any record is selected). The region announces whenever
   * this value changes to a non-empty string.
   */
  message: string | null | undefined;
  /**
   * When true, announce assertively (`role="alert"`) for safety / compliance /
   * real-time transitions that must interrupt. Defaults to polite
   * (`role="status"`).
   */
  assertive?: boolean;
  /** Optional test id for the live region node. */
  'data-testid'?: string;
}

export function DomainAnnouncer({
  message,
  assertive = false,
  'data-testid': testId,
}: DomainAnnouncerProps) {
  // Hold the announced text in state and only update it when the incoming
  // message changes to a new non-empty value. Writing the same text back would
  // not re-trigger a screen-reader read; writing a changed value does.
  const [announced, setAnnounced] = React.useState('');
  const lastRef = React.useRef('');

  React.useEffect(() => {
    const next = (message ?? '').trim();
    if (next && next !== lastRef.current) {
      lastRef.current = next;
      setAnnounced(next);
    }
  }, [message]);

  return (
    <div
      // role="status" → aria-live="polite"; role="alert" → aria-live="assertive".
      // Set aria-live explicitly too so the intent is unambiguous to every AT.
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      aria-atomic="true"
      className="sr-only"
      data-testid={testId}
    >
      {announced}
    </div>
  );
}

DomainAnnouncer.displayName = 'DomainAnnouncer';
